import React, {useState, useEffect} from "react";

import Stockchart from "../../../components/Stockchart";

import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";

import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import {Card} from "reactstrap";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {useSearchParams} from "react-router-dom";
import {formatStockChartConfig, toSnakeCase} from "../../../utils/Helpers";
import {
    allCapacityByDataCenterTrendDefs,
    colDateDef,
    allCapacityByBusinessUnitTrendDefs
} from "../../../utils/gridColumnDefs/storage/enterpriseTrends";
import {chartBUTypes} from "../../../utils/utils";

import {genericColors, enterpriseTrendColors} from "../../../utils/chartUtils";

import {useVsiAppContext} from "../../../stores/VsiApp";
import { typeDefs, typeLines } from "../../../utils/gridColumnDefs/businessUnits/buTrends";

const possibleAdditionals = ['Data Center', 'Device Type', 'Classification', 'Space Type', 'Performance Type'];

const chartMetrics = [
    {value: 'all_business_units', label: 'All Business Units', route: '_by_all_business_units_proc', yAxis: 'GiB'},
    {value: 'by_business_unit', label: 'By Business Unit', route: '_by_business_unit_proc', yAxis: 'Ratio'},
];

const ratioChartMetrics = [
    {value: 'all_business_units', label: 'All Business Units', yAxis: 'GiB'},
    {value: 'by_business_unit', label: 'By Business Unit', yAxis: 'GiB'},
];

const allBuSpaceTypes = [
	{ value: 'used', label: 'Capacity Used' },
	{ value: 'capacity', label: 'Capacity Allocated' },
	{ value: 'capacity_cost', label: 'Capacity Cost' },
    { value: 'cpu', label: 'CPU' },
    { value: 'cost_per_cpu', label: 'CPU Cost' },
    { value: 'memory', label: 'Memory' },
    { value: 'cost_per_memory', label: 'Memory Cost' },
    { value: 'total_cost', label: 'Total Cost' }
];

export default function BUTrends({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const type = searchParams.get('type') || 'used';
    let defaultType = chartBUTypes.find(({value}) => type === value) || chartBUTypes[0];
    const [chartType, setChartType] = useState(defaultType);

    let chosenMetrics = chartType.value === 'data_reduction' ? ratioChartMetrics : chartMetrics;

    const metric = searchParams.get('metric') || 'all_business_units';
    let defaultMetric = chosenMetrics.find(({value}) => metric === value) || chosenMetrics[0];

    const [chartMetric, setChartMetric] = useState(defaultMetric);
    const [activeMetricSelection, setActiveMetricSelection] = useState(chosenMetrics);

    const [chartData, setChartData] = useState([]);
    const [chartPerfData, setChartPerfData] = useState([]);
    const [chartDefs, setChartDefs] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({
        colors: genericColors
    });

    const [additionalChartOption, setAdditionalChartOption] = useState({});
    const [additionalChartOptionSelection, setAdditionalChartOptionSelection] = useState([]);
    const [additionalChartOptionLabel, setAdditionalChartOptionLabel] = useState('');

    const getRouteForSelection = route => {
        if (searchParams.get('type')) {
            if (!searchParams.get('metric') || searchParams.get('metric') === 'all_business_units') {
                route = '_by_all_business_units';
            } else {
                route = `_${searchParams.get('metric')}`;
            }
            route += '_proc';
        }
        let routeToSearch = `/api/v6/report/enterprise_trends${route}?client_id=${client.clientId}&period_id=${periodId}`;

        if (chartMetric.value === 'by_business_unit' && searchParams.get('business_unit')) {
            routeToSearch += `&business_unit=${searchParams.get('business_unit')}`;
        }

        return routeToSearch;
    };

    const [data, {isLoading}] = useDataWithRoute(getRouteForSelection(chartMetric.route))

    const addLabel = searchParams.get(toSnakeCase(additionalChartOptionLabel));

    useEffect(() => {
        setChartData(data?.recordsets[0]);
        setChartPerfData(data?.recordsets[1]);
        handleStateWithData(data?.recordsets || [[], []]);
        setChartDefs(defineColumnDefs(chartMetric.value));
    }, [data, addLabel, chartType, activeMetricSelection]);

    const onTypeChange = (type) => {
        setChartType(type);
        setActiveMetricSelection(chartMetrics);
        searchParams.set('type', type.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onMetricChange = async (metric) => {
        setChartMetric(metric);
        searchParams.set('metric', metric.value);
        searchParams.delete('business_unit');
        setSearchParams(searchParams, {replace: true});
        getRouteForSelection(metric.route);
    };

    const onAdditionalOptionChange = async (option) => {
        setAdditionalChartOption(option);
        searchParams.set(toSnakeCase(additionalChartOptionLabel), option.value);
        setSearchParams(searchParams, {replace: true});

    };

    const setAllAdditionalParams = (selection, metric) => {
        let label = additionalChartOptionLabel;
        let select = [...additionalChartOptionSelection];
        let addtl = {...additionalChartOption};
        if (additionalChartOptionLabel !== metric) {
            label = metric;
        }
        if (additionalChartOptionLabel === '' || additionalChartOptionLabel !== metric || selection.length > 0) {
            select = [...selection];
            addtl = {...select[0]};
        }
        possibleAdditionals.forEach(additional => {
            if (additional !== metric) {
                searchParams.delete(toSnakeCase(additional));
                setSearchParams(searchParams);
            }
        });
        let search = searchParams.get(toSnakeCase(metric));
        if (search !== addtl.value) {
            addtl = {...select.find(({value}) => value === search) || addtl}
        }

        setAdditionalChartOptionSelection(select);
        setAdditionalChartOptionLabel(label);
        setAdditionalChartOption(addtl);
    };
    
    const defineColumnDefs = (metric) => {
        let defsArray = [...chartDefs];

        switch (metric) {
            case "by_business_unit":
                defsArray = colDateDef.concat(allCapacityByDataCenterTrendDefs).concat(typeDefs[chartType.value])
                break;
            case "all_business_units":
                defsArray = colDateDef.concat(allCapacityByBusinessUnitTrendDefs).concat(typeDefs[chartType.value])
                break;
            default:
                break;
        }

        return defsArray;
    };

    const handleStateWithData = data => {
        let config = null;
        let sortedData = data?.[0]?.sort((a, b) => new Date(a.collection_date) - new Date(b.collection_date));

        switch (chartMetric.value) {
            case "by_business_unit":
                config = formatStockChartConfig(sortedData || [], typeLines[chartType.value] || [])
                setAllAdditionalParams((data?.[1] || []).map(datum => {
                    return {
                        value: datum.business_unit,
                        label: datum.business_unit,
                        route: `business_unit=${datum.business_unit}`
                    };
                }), 'Business Unit');
                setChartConfig({
                    colors: enterpriseTrendColors,
                    yAxis: {
                        title: {
                            text: chartType.value.includes('cost') ? '$ Dollars' : 'GiB'
                        },
                    }
                });

                setFormattedChartData(config.series);
                break;
            case "all_business_units":
                let findBu = {};
                const foundBuData = data[0]?.map(datum => {
                    if (!findBu[datum.business_unit]) {
                        findBu[datum.business_unit] = true;
                    }
                    const returnDatum = {}

                    for (let dtKey in datum) {
                        // check if in the key (no prototypes) and if it's capitalized
                        if (datum.hasOwnProperty(dtKey)) {
                            // lowercasing...
                            returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                        }
                    }
                    return returnDatum;
                });
                let includeDollar = chartType.value.includes('cost') ? '$' : ''
                const fieldsBu = Object.keys(findBu).map(bu => ({
                    name: bu,
                    field: (searchParams.get('type') || allBuSpaceTypes[0].value),
                    dataLabels: {format: `${includeDollar}{point.y:,.0f}`},
                    tooltip: {pointFormat: `{series.name}: <b>${includeDollar}{point.y:,.0f}</b>`},
                }));
                config = formatStockChartConfig(foundBuData || [], fieldsBu, 'business_unit')
                setChartConfig({
                    ...chartConfig,
                    colors: enterpriseTrendColors,
                    yAxis: {
                        title: {
                            text: chartType.value.includes('cost') ? '$ Dollars' : 'GiB'
                        },
                    }
                });
                
                setFormattedChartData(config?.series);
                setAllAdditionalParams([], '');
                break;
            default:
            // nothing
        }
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) filteredData.push(node.data);
        });

        handleStateWithData([filteredData]);
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`${chartType.label} Trend ${chartMetric.label}`}</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 pb-2 d-flex"}>
                    <VsiLabeledSelect options={activeMetricSelection}
                                      isDisabled={isLoading}
                                      label={'Metric'}
                                      minWidth={200}
                                      maxWidth={500}
                                      onChange={onMetricChange}
                                      defaultValue={chartMetric}/>
                    {additionalChartOptionLabel && (
                        <VsiLabeledSelect options={additionalChartOptionSelection}
                                        isDisabled={isLoading}
                                        label={additionalChartOptionLabel}
                                        minWidth={200}
                                        maxWidth={600}
                                        onChange={onAdditionalOptionChange}
                                        defaultValue={additionalChartOption}/>
                    )}
                    <VsiLabeledSelect options={allBuSpaceTypes}
                                    isDisabled={isLoading}
                                    label={'Type'}
                                    minWidth={225}
                                    onChange={onTypeChange}
                                    defaultValue={chartType}/>
                </div>
            </VsiBubble>

            <VsiBubble
                unit={chartType.value}
                autobotInfo={{
                    secondaryMetric: [chartMetric.value, additionalChartOption.value],
                    yAxes: chartConfig.yAxis
                }}
            >
                <Stockchart series={formattedChartData} config={chartConfig}/>
            </VsiBubble>
            <VsiBubble unit={chartType.value}
                       autobotInfo={{secondaryMetric: [chartMetric.value, additionalChartOption.value]}}>
                <Grid
                    addStyle
                    data={(chartType.value === 'performance' && chartMetric.value === 'history') ? chartPerfData : chartData}
                    columnDefs={chartDefs}
                    saveParams={{
                        type: chartType.value,
                        metric: chartMetric.value,
                    }}
                    filterChanged={filterChanged}/>
            </VsiBubble>

        </Card>
    );
}
